import {
	AbstractJastVisitor,
	type AstNode,
	COMPOUND_OPERATOR_AND,
	type CompoundClause,
	isOperandNode,
	PROJECT_FIELD,
	type TerminalClause,
} from '@atlaskit/jql-ast';
import { TEXT_FIELDS } from '../../../common/constants.tsx';
import { getOperandValues } from '../get-operand-values.tsx';

export type Result = {
	count: number;
	isTooComplex: boolean;
};

/**
 * Returns all AST terminal clauses that match the provided field's JQL term (case-insensitive)
 */
export class JqlValuesCollectingVisitor extends AbstractJastVisitor<Result> {
	private projectContext: string | undefined;

	constructor(projectContext?: string) {
		super();
		this.projectContext = projectContext;
	}

	visitTerminalClause = (terminalClause: TerminalClause): Result => {
		if (terminalClause.operand == null) {
			return {
				count: 0,
				isTooComplex: true,
			};
		}

		if (terminalClause.field.value === TEXT_FIELDS) {
			return {
				count: 0,
				isTooComplex: false,
			};
		}

		const values = getOperandValues(terminalClause.operand);
		if (terminalClause.field.value === PROJECT_FIELD) {
			if (values.length === 1 && values[0].toLowerCase() === this.projectContext?.toLowerCase()) {
				// Not count if is only the current project
				return {
					count: 0,
					isTooComplex: false,
				};
			}
		}

		return {
			count: values.length,
			isTooComplex: false,
		};
	};

	visitCompoundClause = (compoundClause: CompoundClause): Result => {
		const operator = compoundClause.operator.value;

		if (operator === COMPOUND_OPERATOR_AND) {
			return compoundClause.clauses.reduce<Result>(
				(result, clause) => this.aggregateResult(clause.accept(this), result),
				this.defaultResult(),
			);
		}

		return { count: 0, isTooComplex: true };
	};

	protected shouldVisitNextChild(node: AstNode): boolean {
		// Skip visiting operand subtrees, which aren't needed for clause collection and can make a big impact on performance
		return !isOperandNode(node);
	}

	protected aggregateResult(aggregate: Result, nextResult: Result): Result {
		return {
			count: aggregate.count + nextResult.count,
			isTooComplex: aggregate.isTooComplex || nextResult.isTooComplex,
		};
	}

	protected defaultResult(): Result {
		return {
			count: 0,
			isTooComplex: false,
		};
	}
}
